import { useEffect, useState } from "react";
import { ArrowUpRight, Folder, MessageCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadHierarchy, type HierarchyCatalog, type HierarchySelection } from "./api";
import { accountHistoryUrl, hierarchyUrl, transcriptUrl } from "./urls";
import { CreateConversation } from "./CreateConversation";
import { CreateLocation } from "./CreateLocation";
import { ArtifactBrowser } from "./ArtifactBrowser";

export function HierarchyBrowser({ selection = {} }: { selection?: HierarchySelection }) {
  const { accountId, workspaceId, projectId } = selection;
  const key = JSON.stringify([accountId, workspaceId, projectId]);
  const [revision, setRevision] = useState(0);
  const [search, setSearch] = useState("");
  const [state, setState] = useState<{ key: string; catalog?: HierarchyCatalog; failed?: boolean }>();
  useEffect(() => {
    const controller = new AbortController();
    setState(undefined);
    setSearch("");
    void loadHierarchy({ accountId, workspaceId, projectId }, controller.signal).then(
      (catalog) => { if (!controller.signal.aborted) setState({ key, catalog }); },
      () => { if (!controller.signal.aborted) setState({ key, failed: true }); },
    );
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.removeEventListener("focus", refresh); };
  }, [accountId, workspaceId, projectId, key, revision]);

  const current = state?.key === key ? state : undefined;
  if (!current) return <div className="p-8 text-sm text-[#52606e]" role="status">Loading workspaces…</div>;
  if (current.failed || !current.catalog) return (
    <main className="mx-auto max-w-xl px-6 py-12">
      <h1 className="text-xl font-semibold">Workspace unavailable</h1>
      <p className="mt-3 text-sm leading-6 text-[#52606e]">Check the selected account, or try again when you're connected.</p>
      <Button className="mt-5" onClick={() => setRevision((value) => value + 1)}>Try again</Button>
      <a className="ml-5 text-sm underline" href="/spaces">Your workspaces</a>
    </main>
  );
  const { catalog } = current;
  const account = catalog.scope.accountId;
  const rows = catalog.workspace ? catalog.projects : catalog.workspaces;
  const filteredRows = rows.filter(row => row.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  return (
    <main className="w-full text-[#20272b]">
      <div className="workspace-hero">

      <nav aria-label="Workspace location" className="mb-7 flex flex-wrap justify-center gap-3 text-xs text-[#52606e]">
        <a className="rounded underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-[#647bd6]" href={hierarchyUrl(account)}>Workspaces</a>
        {catalog.workspace ? <a className="rounded underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-[#647bd6]" href={hierarchyUrl(account, catalog.workspace.id)}>{catalog.workspace.name}</a> : null}
        {catalog.project ? <span aria-current="page">{catalog.project.name}</span> : null}
      </nav>
      <h1 className="mx-auto max-w-3xl">{catalog.project?.name ?? catalog.workspace?.name ?? "What will you make today?"}</h1>
      {!catalog.workspace ? <p className="mt-3 text-sm text-[#52606e]">Choose a workspace. Give your next idea a place to grow.</p> : null}
      {catalog.workspace ? <p className="mt-3 text-sm text-[#52606e]">{catalog.project ? "Your conversations and files, all in one place." : "Start a project or keep a conversation going."}</p> : null}
      {!catalog.project && (catalog.workspace ? catalog.workspace.accessRole === "admin" : catalog.scope.role === "owner" || catalog.scope.role === "manager") ?
        <div id={catalog.workspace ? "create-project" : "create-workspace"} className="mx-auto mt-8 max-w-xl rounded-2xl border border-white/90 bg-white/95 px-6 text-left shadow-[0_10px_35px_rgba(53,80,134,0.08)]">
          <CreateLocation key={key} selection={{ accountId: account, workspaceId: catalog.workspace?.id }} identity={catalog.scope} />
        </div> : null}
      </div>
      <div className="workspace-collection mx-auto max-w-[88rem]">
      {!catalog.workspace && (catalog.scope.role === "owner" || catalog.scope.role === "manager") ?
        <a className="mt-5 inline-block text-sm underline" href={accountHistoryUrl(account)}>Director history</a> : null}

      {!catalog.project ? (
        <section className="mt-8" aria-label={catalog.workspace ? "Projects" : "Workspaces"}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-lg font-semibold tracking-tight">{catalog.workspace ? "Projects" : "Your workspaces"}</h2>
            {rows.length > 0 ? <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-3 text-[#68717c]" aria-hidden="true" />
              <input type="search" aria-label={catalog.workspace ? "Search projects" : "Search workspace cards"} placeholder={catalog.workspace ? "Search projects" : "Search workspaces"} value={search} onChange={event => setSearch(event.target.value)} className="workspace-search max-w-64" />
            </div> : null}
          </div>
          {filteredRows.length ? <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredRows.map((row, index) => <li key={row.id}>
              <a className="workspace-project-card group" href={catalog.workspace ? hierarchyUrl(account, catalog.workspace.id, row.id) : hierarchyUrl(account, row.id)}>
                <div aria-hidden="true" className={`flex h-32 items-center justify-center ${index % 3 === 0 ? "bg-[#eaf1ff]" : index % 3 === 1 ? "bg-[#eeedf9]" : "bg-[#e8f3f3]"}`}>
                  <Folder size={34} strokeWidth={1.25} className="text-[#6a80ab]" />
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <span className="min-w-0 truncate text-sm font-semibold">{row.name}</span>
                  <ArrowUpRight size={16} className="shrink-0 text-[#8d98aa] group-hover:text-[#405de6]" aria-hidden="true" />
                </div>
              </a>
            </li>)}
          </ul> : <p className="rounded-xl border border-dashed border-[#dce2ec] p-8 text-center text-sm text-[#52606e]">{rows.length ? "No matching projects or workspaces." : catalog.workspace ? "No projects available." : "No workspaces available in this account."}</p>}
        </section>
      ) : null}
      {catalog.workspace ? <section className="mt-10 rounded-2xl border border-[#e3e7ee] bg-white p-5 sm:p-6" aria-label="Conversations">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><MessageCircle size={18} className="text-[#7082a0]" aria-hidden="true" />Conversations</h2>
        {catalog.canCreateThread ? <CreateConversation key={key} selection={{ accountId: account, workspaceId: catalog.workspace.id, projectId: catalog.project?.id }} /> : <p className="mb-4 text-sm text-[#52606e]">You have read-only access here.</p>}
        {catalog.threads.length ? <ul className="divide-y divide-[#cfe4f1] border-y border-[#cfe4f1]">{catalog.threads.map((thread) => <li key={thread.id}><a className="block rounded-md px-2 py-4 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]" href={transcriptUrl({ accountId: account, workspaceId: thread.workspaceId, projectId: thread.projectId, threadId: thread.id })}>{thread.name}</a></li>)}</ul> : <p className="py-4 text-sm text-[#52606e]">No conversations here yet.</p>}
      </section> : null}
      {catalog.workspace ? <ArtifactBrowser key={key} location={{ accountId: account, workspaceId: catalog.workspace.id, projectId: catalog.project?.id }} /> : null}
      </div>
    </main>
  );
}
